import { defineStore } from 'pinia';
import { ref, Component, computed, markRaw } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { getComponentName } from '@/utils/vue-related.ts';
import type { DynamicModalOwner } from '@/store/hooks/dynamicModalOwner.ts';
import {
    ComponentEmitHandlers,
    ComponentProps,
    ComponentSlots,
    ComponentWithEmits,
    DrawerSide,
    DrawerSize,
    isModalSlotOptions,
    ModalMode,
    ModalSlot,
} from './types.ts';

/**
 * A record of data for a given modal, kept in the store.
 */
export type ModalData = {
    // unique id for the modal, random generated
    id: string;

    // "business" name for the modal, to differentiate between different modals
    name: string;

    // the component to render
    component: Component;

    // props to pass to the component
    props?: ComponentProps<Component>;

    // event handlers to pass to the component
    emits?: ComponentEmitHandlers;

    // slots to pass to the component
    slots?: ComponentSlots;

    // date when the modal was opened, used to sort the modals
    openedAt: Date;

    // if true, won't open more than one modal with the same name
    unique?: boolean;

    // if true, will show an extra close button in the top right corner of the modal, regardless of the modal content component (the button is displayed by the modal container). This is useful if you want to keep the modal content component without any close button, e.g. to use it as a standalone component.
    showExtraCloseButton?: boolean;

    // how the entry is presented: a centered box (modal) or an edge drawer
    mode: ModalMode;

    // edge a drawer is anchored to and slides in from. Set only for drawers;
    // undefined for modals, which have no anchored edge.
    drawerSide?: DrawerSide;

    // responsive design-system size. Undefined uses the default drawer width.
    drawerSize?: DrawerSize;

    // if true, clicking the tinted backdrop closes the entry. Defaults to true for
    // drawers and false for modals.
    closeOnOverlayClick: boolean;

    // if true, pressing Escape closes the entry. Defaults to true for drawers so a
    // dialog is dismissible by keyboard; set false for a drawer that must not be
    // dismissed without an explicit choice.
    closeOnEscape: boolean;

    // Optional accessible name owned by the rendered modal/drawer panel.
    ariaLabel?: string;

    // Optional id of an element that labels the rendered modal/drawer panel.
    ariaLabelledBy?: string;

    // Optional lifecycle owner for entries opened from a disposable workflow.
    owner?: DynamicModalOwner;
};

/**
 * Store to keep information about which modals have been opened.
 * Note: only opened modals are stored, closed modals are removed from the store.
 */

/**
 * Marks slot components as raw to prevent Vue from making them reactive,
 * which would trigger unnecessary re-renders and compat warnings.
 */
export function markSlotComponentsRaw(slots?: ComponentSlots): ComponentSlots | undefined {
    if (!slots) {
        return undefined;
    }

    return Object.fromEntries(
        Object.entries(slots).map(([key, slot]: [string, ModalSlot]) => {
            if (typeof slot === 'string') {
                return [key, slot];
            }

            if (isModalSlotOptions(slot)) {
                return [key, { ...slot, component: markRaw(slot.component) }];
            }

            return [key, markRaw(slot)];
        })
    );
}

export type OpenModalOptions<T extends ComponentWithEmits> = {
    component: T;
    name?: string;
    props?: ComponentProps<T>;
    emits?: ComponentEmitHandlers<T>;
    slots?: ComponentSlots;
    unique?: boolean;
    showExtraCloseButton?: boolean;
    mode?: ModalMode;
    drawerSide?: DrawerSide;
    drawerSize?: DrawerSize;
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    owner?: DynamicModalOwner;
};

export const useDynamicModalStore = defineStore('modal', () => {
    const modalsObj = ref<Record<string, ModalData>>({});

    function openModal<T extends ComponentWithEmits>({
        name,
        component,
        props,
        slots,
        emits,
        unique = true,
        showExtraCloseButton = false,
        mode = ModalMode.Modal,
        drawerSide,
        drawerSize,
        closeOnOverlayClick,
        closeOnEscape,
        ariaLabel,
        ariaLabelledBy,
        owner,
    }: OpenModalOptions<T>) {
        const modalName = name ?? getComponentName(component);
        const isDrawer = mode === ModalMode.Drawer;
        const overlayClosesEntry = closeOnOverlayClick ?? isDrawer;
        const escapeClosesEntry = closeOnEscape ?? isDrawer;
        // A drawer needs an anchored edge (defaulting to the right); a modal has none.
        const resolvedDrawerSide = isDrawer ? drawerSide ?? DrawerSide.Right : undefined;
        // Size applies only to drawers; modals are centered boxes sized by their content.
        const resolvedDrawerSize = isDrawer ? drawerSize : undefined;
        // If modal is unique, check if it already exists. If it does, don't open it again.
        if (unique) {
            const existingModal = Object.values(modalsObj.value).find(
                (modal) => modal.name === modalName
            );
            if (existingModal) {
                return;
            }
        }

        const id = uuidv4();
        modalsObj.value = {
            ...modalsObj.value,
            [id]: {
                id,
                name: modalName,
                component: markRaw(component),
                props,
                emits,
                slots: markSlotComponentsRaw(slots),
                openedAt: new Date(),
                showExtraCloseButton,
                mode,
                drawerSide: resolvedDrawerSide,
                drawerSize: resolvedDrawerSize,
                closeOnOverlayClick: overlayClosesEntry,
                closeOnEscape: escapeClosesEntry,
                ariaLabel,
                ariaLabelledBy,
                owner: owner ? markRaw(owner) : undefined,
            },
        };
    }

    function closeModal(id: string) {
        modalsObj.value = Object.fromEntries(
            Object.entries(modalsObj.value).filter(([key]) => key !== id)
        );
    }

    // Closes every entry sharing this name. With `unique: true` (the default) at
    // most one matches; callers that open non-unique entries under one name get
    // them all closed in a single call.
    function closeModalsByName(name: string) {
        modalsObj.value = Object.fromEntries(
            Object.entries(modalsObj.value).filter(([, modal]) => modal.name !== name)
        );
    }

    function closeModalByName(name: string) {
        closeModalsByName(name);
    }

    function closeModalsByOwner(owner: DynamicModalOwner) {
        modalsObj.value = Object.fromEntries(
            Object.entries(modalsObj.value).filter(([, modal]) => modal.owner?.id !== owner.id)
        );
    }

    return {
        // List of modals, sorted by the time they were opened.
        modals: computed(() =>
            Object.values(modalsObj.value).sort(
                (a, b) => a.openedAt.getTime() - b.openedAt.getTime()
            )
        ),
        openModal,
        closeModal,
        closeModalByName,
        closeModalsByName,
        closeModalsByOwner,
    };
});
